import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth, loadUserProfile } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { takeReturnTo } from "@/lib/returnTo";
import BricksLoader from "@/components/BricksLoader";

function oauthErrorFromLocation() {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const code = search.get("error") || hash.get("error");
  if (!code) return null;
  const description = search.get("error_description") || hash.get("error_description");
  return description || code;
}

/** Consume PKCE ?code= or implicit #access_token (email confirm type=signup).
 *  Google and Apple OAuth both land here from redirectTo https://app.brxs.ca/auth/callback.
 */
async function consumeUrlSession() {
  const { data: { session: existing } } = await supabase.auth.getSession();
  if (existing) return existing;

  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  const pkceCode = search.get("code");
  if (pkceCode) {
    const { data, error } = await supabase.auth.exchangeCodeForSession(pkceCode);
    if (!error && data?.session) return data.session;
  }

  const access_token = hash.get("access_token");
  const refresh_token = hash.get("refresh_token");
  if (access_token && refresh_token) {
    const { data, error } = await supabase.auth.setSession({ access_token, refresh_token });
    if (!error && data?.session) return data.session;
  }

  return null;
}

export default function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    let cancelled = false;
    let subscription = null;
    let timer = null;

    const fail = (reason) => {
      if (cancelled) return;
      navigate(`/login?error=${encodeURIComponent(reason || "Sign-in failed")}`, { replace: true });
    };

    const succeed = async (session) => {
      if (cancelled || !session) return false;
      try {
        let profile;
        try {
          profile = await loadUserProfile(session);
        } catch {
          await api.post("/auth/ensure-profile");
          profile = await loadUserProfile(session);
        }
        if (cancelled) return true;
        setUser(profile);
        const back = takeReturnTo();
        const dest = profile?.needs_onboarding ? "/onboarding" : (back || "/");
        navigate(dest, { replace: true });
        return true;
      } catch {
        fail("Could not load your account.");
        return true;
      }
    };

    (async () => {
      const oauthError = oauthErrorFromLocation();
      if (oauthError) {
        fail(oauthError);
        return;
      }

      const session = await consumeUrlSession();
      if (await succeed(session)) return;

      const { data } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
        if (nextSession) {
          if (subscription) subscription.unsubscribe();
          if (timer) clearTimeout(timer);
          await succeed(nextSession);
        } else if (event === "SIGNED_OUT") {
          fail("Signed out.");
        }
      });
      subscription = data.subscription;

      timer = setTimeout(() => fail("Sign-in timed out. Try again."), 8000);
    })();

    return () => {
      cancelled = true;
      if (subscription) subscription.unsubscribe();
      if (timer) clearTimeout(timer);
    };
  }, [navigate, setUser]);

  return (
    <div className="flex h-screen items-center justify-center bg-background">
      <BricksLoader variant="assemble" size={64} />
    </div>
  );
}
